// src/encounter/use-video-join-window.ts
//
// The video join window (lib/video-join-window.ts), re-read as time passes: the encounter page stays open across the
// start, so Connect has to appear 10 minutes before it without a reload. Flutter re-checks on a 10-second timer
// (`encounter_screen.dart:1220` startTimer); this wakes once, at the next change, instead.

import { useEffect, useMemo, useState } from 'react';
import { practiceNow } from '@/shared/lib/practice-time';
import { nextVideoJoinChange, videoJoinWindow, type VideoJoinWindow } from './lib/video-join-window';

/** Re-check at least hourly, so a long wait does not ride on one timer (sleep, clock changes). */
const MAX_WAIT_MS = 60 * 60 * 1000;

export function useVideoJoinWindow(
  start: string | null | undefined,
  end: string | null | undefined,
  statusCode: string | null | undefined,
): VideoJoinWindow {
  // `tick` only triggers a re-read; the clock is read fresh each time, so a start that loads late is judged against now.
  const [tick, setTick] = useState(0);
  const joinWindow = useMemo(
    () => videoJoinWindow({ start, end, statusCode, now: practiceNow() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `tick` is the re-read trigger
    [start, end, statusCode, tick],
  );

  useEffect(() => {
    const next = nextVideoJoinChange(joinWindow);
    if (!next) return;
    // The same clock the rule reads (practiceNow), so the timer wakes at the change the rule will see.
    const wait = Math.min(Math.max(next.getTime() - practiceNow().getTime(), 0), MAX_WAIT_MS);
    const id = setTimeout(() => setTick((t) => t + 1), wait);
    return () => clearTimeout(id);
  }, [joinWindow]);

  return joinWindow;
}
